import React, { useState, useEffect, useRef, useCallback } from 'react';
import './PlatformDemo.css';

// Import screenshots
import dashboardImg from '../assets/demo/dashboard.png';
import propertiesImg from '../assets/demo/properties.png';
import dossierImg from '../assets/demo/dossier.png';
import analyticsImg from '../assets/demo/analytics.png';
import applicantsImg from '../assets/demo/applicants.png';
import messagesImg from '../assets/demo/messages.png';
import viewingsImg from '../assets/demo/viewings.png';

const features = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <rect x="2" y="2" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="11" y="2" width="7" height="4" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="2" y="11" width="7" height="4" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="11" y="8" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
    image: dashboardImg,
    title: 'Everything at a glance',
    description: 'Your bento-style dashboard shows occupancy, rent health, open requests, and action items. No digging through menus.',
  },
  {
    id: 'properties',
    label: 'Properties',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M3 17V9l7-5.5L17 9v8" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <rect x="7.5" y="12" width="5" height="5" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
    image: propertiesImg,
    title: 'List and manage properties',
    description: 'Add photos, set rent, toggle availability. Each listing has its own page with tenants, viewings, and payment history.',
  },
  {
    id: 'screening',
    label: 'AI Screening',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <rect x="3" y="2" width="14" height="16" rx="2" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M7 7h6M7 10h6M7 13h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
        <circle cx="15" cy="15" r="3" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M14 15l1 1 2-2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    image: dossierImg,
    title: 'AI-powered screening',
    description: 'Documents are extracted, risk-scored, and summarized automatically. Credit checks, income verification, and references in one dossier.',
  },
  {
    id: 'applicants',
    label: 'Applications',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="7" r="3.5" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M4 17c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    image: applicantsImg,
    title: 'Applicant pipeline',
    description: 'Track every applicant from viewing to lease. Filter by property, review documents, request info, approve or decline.',
  },
  {
    id: 'viewings',
    label: 'Scheduling',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <rect x="2.5" y="4" width="15" height="13" rx="2" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M2.5 8h15M6 2v4M14 2v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
    image: viewingsImg,
    title: 'Viewings and tours',
    description: 'Set available time slots, tenants book online. Calendar view with confirmations and reminders built in.',
  },
  {
    id: 'messages',
    label: 'Messaging',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <rect x="2" y="3" width="16" height="11" rx="2" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M6 8h8M6 11h5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
        <path d="M6 14l-2 3v-3" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      </svg>
    ),
    image: messagesImg,
    title: 'Built-in messaging',
    description: 'Every conversation tied to a property and tenant. No more scattered emails or texts. Support threads included.',
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <rect x="3" y="11" width="3" height="6" rx="0.5" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="8.5" y="7" width="3" height="10" rx="0.5" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="14" y="3" width="3" height="14" rx="0.5" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
    image: analyticsImg,
    title: 'Portfolio analytics',
    description: 'Track occupancy, rent collection, and growth across all your properties. See what needs attention at a glance.',
  },
];

const ROTATE_MS = 5000;

export default function PlatformDemo() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const advance = useCallback(() => {
    setActive(prev => (prev + 1) % features.length);
  }, []);

  useEffect(() => {
    if (paused) return;
    timerRef.current = setInterval(advance, ROTATE_MS);
    return () => clearInterval(timerRef.current);
  }, [paused, advance]);

  const handleSelect = (i) => {
    setActive(i);
    setPaused(true);
    // Resume auto-rotation after 15s of inactivity
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setPaused(false), 15000);
  };

  const feat = features[active];

  return (
    <section id="steps" className="platform-demo">
      <p className="eyebrow-tag">See the platform</p>
      <h2>Built for landlords who want less hassle.</h2>

      <div className="demo-layout">
        {/* Tab list */}
        <div className="demo-tabs" role="tablist">
          {features.map((f, i) => (
            <button
              key={f.id}
              role="tab"
              aria-selected={i === active}
              className={`demo-tab ${i === active ? 'active' : ''}`}
              onClick={() => handleSelect(i)}
            >
              <span className="demo-tab-icon">{f.icon}</span>
              <span className="demo-tab-label">{f.label}</span>
              {i === active && !paused && (
                <span className="demo-tab-progress">
                  <span className="demo-tab-progress-bar" />
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Preview area */}
        <div className="demo-stage">
          <div className="demo-stage-screen">
            <div className="demo-stage-dots">
              <span /><span /><span />
            </div>
            <div className="demo-stage-img-wrap">
              {features.map((f, i) => (
                <img
                  key={f.id}
                  src={f.image}
                  alt={f.title}
                  className={`demo-stage-img ${i === active ? 'active' : ''}`}
                />
              ))}
            </div>
          </div>
          <div className="demo-stage-info" key={feat.id}>
            <h3>{feat.title}</h3>
            <p>{feat.description}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
